/* eslint-disable prettier/prettier */
import { Dispatch, useEffect, useMemo, useState } from 'react';
import * as Sentry from '@sentry/react';
import { Action } from '@/state/actions';
import getCookies from './cookies';
import fetch from './fetch';
import { maybeSetSelectedAccount, useSetSession } from './session';
import actions from '../state/actions';
import { RelationshipAccounts } from './types';

/**
 * Checks if a magic token has been provided and processes it.
 * If logged in, fetches associated accounts.
 */
export const useAuthAndAccountSetup = (dispatch: Dispatch<Action>) => {
  const [checkingAuth, setCheckingAuth] = useState(true);
  const setSession = useSetSession(dispatch);

  const query = new URLSearchParams(window.location.search);
  // only get magic token on page load
  const magicToken = useMemo(() => {
    return query.get('token');
  }, []);

  useEffect(() => {
    setCheckingAuth(true);
    dispatch(actions.setToken(magicToken));

    const newQuery = new URLSearchParams(query);
    newQuery.delete('token');
    const newQueryString = newQuery.toString();
    history.pushState(
      null,
      '',
      `${location.href.split('?')[0]}${
        newQueryString ? '?' + newQueryString : ''
      }`,
    );

    const whoamiCookie = () => getCookies()['__Host-whoami'];
    const whoami = () => JSON.parse(whoamiCookie() || '{}') as Record<string, unknown>;
    void processMagicToken(magicToken, whoami, dispatch)
      .then(() => setSession()) // set session has to come first before getting relationship accounts as relationship accounts lives on auth state
      .then(async () => {
        // without this, we can potentially get a rejected promise error on load
        const csrfCookie = () => getCookies()['__Host-csrf'];
        const whoamiCookie = () => getCookies()['__Host-whoami'];
        if (whoamiCookie() && csrfCookie()) {
          const accounts: RelationshipAccounts = await fetch
            .json('/api/get_relationship_accounts', {
              method: 'GET',
            })
            .catch(() => {
              dispatch(
                actions.async.setLoading({
                  key: 'patientData',
                  loadingState: 'error',
                }),
              );
            });

          await maybeSetSelectedAccount(accounts, dispatch);
          dispatch(actions.setRelationshipAccounts(accounts));
        }

        return Promise.resolve();
      })
      .catch()
      .finally(() => setCheckingAuth(false));
  }, [magicToken]);

  return [checkingAuth];
};

async function processMagicToken(
  magicToken: string | null,
  whoami: () => Record<string, unknown>,
  dispatch: Dispatch<Action>,
) {
  if (!magicToken) {
    return;
  }

  const authenticated = 'email' in whoami();
  if (authenticated) {
    void fetch
      .json(`/magic_token?token=${magicToken}`, {
        method: 'DELETE',
      })
      .catch(); // delete magic token if user is already authenticated and using a link with a magic token
    return;
  }

  return fetch
    .json(`/session`, { method: 'POST', body: { token: magicToken } })
    .catch((r) => {
      if (r.json) {
        r.json().then(({ error }: { error: any }) => {
          if (error && typeof error.match === 'function') {
            if (error.match(/expired/)) {
              dispatch(actions.setSessionStatus('magic_link_expired'));
            } else if (error.match(/used/)) {
              dispatch(actions.setSessionStatus('magic_link_used'));
            }
          }
        });
      } else {
        Sentry.captureException(r, {
          extra: {
            message: 'error with no json() method on session create',
          },
        });
      }
    })
    .then((r) => {
      if (r && !r.verified) {
        dispatch(actions.setToken(r.token));
        if (r.verificationType === 'phone_number') {
          dispatch(actions.setSessionStatus('unverified_phone'));
        } else {
          dispatch(actions.setSessionStatus('unverified_birthdate'));
        }
        return Promise.reject();
      }
    });
}
